import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
} from '@angular/core';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { SegmentedButtonType } from '@recruitee/user-interface';
import {
  BoxComponent,
  CollapsibleCardComponent,
  HeadingComponent,
  SegmentedControlComponent,
} from '@recruitee/user-interface';

import { CollapsibleCardAnalyticsDirective } from '../collapsible-card/collapsible-card-analytics.directive';
import { CandidateQuestionsResultsComponent } from './candidate-questions-results/candidate-questions-results.component';
import type {
  GroupedOpenQuestionAnswer,
  OpenQuestionAnswer,
} from './candidate-questions.interfaces';
import type { CandidateQuestionsModuleTranslations } from './translations';
import { CandidateQuestionsTranslations } from './translations';
import { QuestionsMode } from './types';

@Component({
  selector: 'rt-candidate-questions',
  templateUrl: './candidate-questions.component.html',
  styleUrls: ['./candidate-questions.component.less'],
  host: {
    '[class.is-empty]':
      '!this.isReferralQuestionsExist && !this.isQuestionnairesExist && !this.isGroupedScreeningQuestionsExist',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    HeadingComponent,
    SegmentedControlComponent,
    CandidateQuestionsResultsComponent,
    TranslatePipe,
    CollapsibleCardComponent,
    CollapsibleCardAnalyticsDirective,
  ],
})
export class CandidateQuestionsComponent {
  public readonly QuestionsMode: typeof QuestionsMode = QuestionsMode;

  @Input() public visibilityTplRef: TemplateRef<any>;
  @Input() public questionnaires: GroupedOpenQuestionAnswer[];
  @Input() public referralsQuestions: GroupedOpenQuestionAnswer[] = [];
  @Input() public openQuestions: OpenQuestionAnswer[];
  @Input() public groupedScreeningQuestions: GroupedOpenQuestionAnswer[];
  @Input() public canManageVisibility: boolean = false;
  @Input() public canManageCandidates: boolean = false;
  @Input() public canViewQuestionnaires: boolean = false;
  @Input() public canViewOpenQuestions: boolean = false;
  @Input() public hasNewStyling: boolean = false;
  @Input() public referralsEnabled: boolean = false;

  public segmentedButtons: Array<SegmentedButtonType<QuestionsMode>> = [];

  public title: string | undefined;

  public mode: QuestionsMode;

  public get isGroupedScreeningQuestionsExist(): boolean {
    return (
      this.groupedScreeningQuestions &&
      !!this.groupedScreeningQuestions.length &&
      this.groupedScreeningQuestions.every(
        openQuestion => openQuestion.openQuestionAnswers.length > 0,
      ) &&
      this.canViewOpenQuestions
    );
  }

  public get isReferralQuestionsExist(): boolean {
    return this.referralsQuestions && !!this.referralsQuestions.length && this.referralsEnabled;
  }

  public get isQuestionnairesExist(): boolean {
    return this.questionnaires && !!this.questionnaires.length && this.canViewQuestionnaires;
  }

  public get isMoreThenOneSection(): boolean {
    const existSectionCount = [
      this.isQuestionnairesExist,
      this.isReferralQuestionsExist,
      this.isGroupedScreeningQuestionsExist,
    ].filter(Boolean);

    return existSectionCount.length > 1;
  }

  private get defaultMode(): QuestionsMode | undefined {
    if (this.isGroupedScreeningQuestionsExist) {
      return QuestionsMode.GroupedScreeningQuestions;
    } else if (this.isReferralQuestionsExist) {
      return QuestionsMode.ReferralQuestions;
    } else if (this.isQuestionnairesExist) {
      return QuestionsMode.Questionnaires;
    }

    return;
  }

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    @Inject(CandidateQuestionsTranslations)
    public translations: CandidateQuestionsModuleTranslations,
  ) {}

  public ngOnInit() {
    this.setSegmentButtons();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.mode = this.mode || this.defaultMode!;
    this.resolveTitle();

    if ('questionnaires' in changes || 'referralsQuestions' in changes) {
      this.setSegmentButtons();
    }
  }

  public handleSectionButtonClick(mode: QuestionsMode): void {
    this.mode = mode;
    this.setSegmentButtons();
    this.cdRef.detectChanges();
  }

  public setSegmentButtons(): void {
    this.segmentedButtons = [
      {
        isActive: this.mode === QuestionsMode.GroupedScreeningQuestions,
        value: QuestionsMode.GroupedScreeningQuestions,
        label: this.i18n.translate(this.translations.titles.questions),
        isAvailable: this.isGroupedScreeningQuestionsExist,
      },
      {
        isActive: this.mode === QuestionsMode.ReferralQuestions,
        value: QuestionsMode.ReferralQuestions,
        label: this.i18n.translate(<string>this.translations.titles.referralQuestions),
        isAvailable: this.isReferralQuestionsExist,
      },
      {
        isActive: this.mode === QuestionsMode.Questionnaires,
        value: QuestionsMode.Questionnaires,
        label: this.i18n.translate(this.translations.titles.questionnaires),
        isAvailable: this.isQuestionnairesExist,
      },
    ];
  }

  private resolveTitle(): void {
    const i18nKeys: Record<QuestionsMode, string | undefined> = {
      [QuestionsMode.Questionnaires]: this.translations.titles.questionnaires,
      [QuestionsMode.ReferralQuestions]: this.translations.titles.referralQuestions,
      [QuestionsMode.GroupedScreeningQuestions]: this.translations.titles.questions,
    };

    this.title = i18nKeys[this.mode];
  }
}
